import { useCallback, useEffect, useReducer, type ChangeEventHandler } from 'react';
import translations from '../../../translations';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import FormGroup from '../../generic/FormGroup';
import ComponentWithTooltip from '../../generic/tooltip/ComponentWithTooltip';
import ClipboardInput from '../../inputs/ClipboardInput';
import { useTenant } from '../../TenantProvider';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import type { FlowResponseAPI } from '../../../types/flow';
import {
    exportFlow,
    getActivated,
    getActivatedForEnvironment,
    shareFlow,
} from '../../../sources/flow';
import type { Environment } from '../../../types/environment';
import { activateFileDownload } from '../../../utils/file';
import { getAllEnvironments } from '../../../sources/environments';
import './css/export.css';
import Loader from '../../loader/Loader';
import type { SingleValue } from 'react-select';
import Select, { createFilter } from 'react-select';
import { getSharedStyles } from '../../../utils/select';

const ExportFlow = () => {
    const tenant = useTenant();
    const dispatchNotification = useNotificationDispatch();

    const [state, dispatchImportExport] = useReducer(reducer, {
        selectedEnvironmentId: null,
        environments: [],
        flows: [],
        selectedFlowId: null,
        sharingToken: null,
        includePasswords: false,
        isProcessing: false,
    });

    const {
        selectedEnvironmentId,
        environments,
        flows,
        selectedFlowId,
        sharingToken,
        includePasswords,
        isProcessing,
    } = state;

    const fetchActivatedFlows = useCallback(
        async (environmentId: string | null) => {
            try {
                let activatedFlows: FlowResponseAPI[] = [];

                if (environmentId) {
                    activatedFlows = await getActivatedForEnvironment(environmentId);
                } else {
                    activatedFlows = await getActivated();
                }

                dispatchImportExport({
                    type: 'flowsLoaded',
                    payload: activatedFlows,
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        },
        [dispatchNotification],
    );

    const onEnvironmentSelected: ChangeEventHandler<HTMLSelectElement> = ({
        target: { value },
    }) => {
        dispatchImportExport({
            type: 'environmentSelected',
            payload: value,
        });

        fetchActivatedFlows(value);
    };

    const onFlowSelected = (e: SingleValue<{ label: string | null; value: string | null }>) => {
        dispatchImportExport({
            type: 'flowSelected',
            payload: e?.value || null,
        });
    };

    const onIncludePasswordsChange: React.ChangeEventHandler<HTMLInputElement> = ({
        target: { checked },
    }) => {
        dispatchImportExport({
            type: 'setIncludePasswords',
            payload: checked,
        });
    };

    const onShareFlow = async () => {
        if (!selectedFlowId) {
            return;
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: true,
        });

        try {
            const { token } = await shareFlow(
                selectedFlowId,
                includePasswords,
                selectedEnvironmentId,
            );
            dispatchImportExport({
                type: 'sharingTokenLoaded',
                payload: token,
            });
        } catch (error) {
            dispatchImportExport({
                type: 'sharingTokenFailed',
            });
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    };

    const onExportFlow = async () => {
        if (!selectedFlowId) {
            return;
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: true,
        });

        try {
            const flowBlob = await exportFlow(
                selectedFlowId,
                includePasswords,
                selectedEnvironmentId,
            );

            const flowName = flows.find((flow) => flow.id.id === selectedFlowId)?.developerName;

            activateFileDownload(flowBlob, `${flowName ?? 'Flow'}.package2`);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: false,
        });
    };

    const environmentOptions = [
        <option value="" key="no environment">
            {translations.ENVIRONMENT_filters_select_environment}
        </option>,
    ].concat(
        environments.map((environment) => (
            <option value={environment.id} key={environment.id}>
                {environment.name}
            </option>
        )),
    );

    const isFlowSelected = selectedFlowId !== null && selectedFlowId !== '';
    const selectedFlow = flows.find((flow) => flow.id.id === selectedFlowId);
    const selectedFlowOption: SingleValue<{ label: string | null; value: string | null }> =
        selectedFlow ? { value: selectedFlow.id.id, label: selectedFlow.developerName } : null;

    useEffect(() => {
        (async () => {
            if (tenant.tenantSettings?.environments) {
                const environments = await getAllEnvironments();
                dispatchImportExport({
                    type: 'environmentsLoaded',
                    payload: environments,
                });
            } else {
                fetchActivatedFlows(null);
            }
        })();
    }, [fetchActivatedFlows, tenant.tenantSettings?.environments]);

    return (
        <div className="export-flow">
            {isProcessing && <Loader message={translations.COMMON_processing} />}
            <div
                className="form-group"
                hidden={!tenant.tenantSettings?.environments}
                data-testid="environmentSelectGroup"
            >
                <select
                    onChange={onEnvironmentSelected}
                    className="select-flow form-control"
                    data-testid="environmentSelect"
                >
                    {environmentOptions}
                </select>
            </div>
            <div className="form-group">
                <Select
                    isDisabled={!!tenant.tenantSettings?.environments && !selectedEnvironmentId}
                    aria-label="Flow Select"
                    required={false}
                    inputId="flow-select-box"
                    styles={{
                        ...getSharedStyles<{
                            label: string | null;
                            value: string | null;
                        }>({
                            container: (baseStyles) => ({
                                ...baseStyles,
                                maxWidth: '45rem',
                            }),
                        }),
                    }}
                    placeholder="Select a flow"
                    onChange={onFlowSelected}
                    value={selectedFlowOption}
                    isLoading={false}
                    options={flows.map((flow) => ({
                        value: flow.id.id,
                        label: flow.developerName,
                    }))}
                    noOptionsMessage={() => 'No results found'}
                    menuPosition="fixed"
                    menuPortalTarget={(() => {
                        const target = document.querySelector('.tab-content-wrapper');
                        return target instanceof HTMLElement ? target : null;
                    })()}
                    isClearable={false}
                    menuShouldScrollIntoView={false}
                    filterOption={createFilter({
                        matchFrom: 'any',
                        stringify: (option) => `${option.label}`,
                    })}
                />
            </div>
            <div className="form-group">
                <ComponentWithTooltip
                    trigger={['hover', 'focus']}
                    tooltipPlacement="bottom-start"
                    tooltipContent={
                        translations.IMPORT_EXPORT_include_passwords_checkbox_description
                    }
                    tooltipClass="export-tooltip"
                    tooltipArrowClass="export-tooltip-arrow"
                    fadeTime={0.2}
                    arrowClearance={8}
                >
                    <label>
                        <input
                            type="checkbox"
                            checked={includePasswords}
                            onChange={onIncludePasswordsChange}
                        />
                        {translations.IMPORT_EXPORT_include_passwords_checkbox_label}
                    </label>
                </ComponentWithTooltip>
            </div>
            <ButtonPrimary
                onClick={onShareFlow}
                disabled={!isFlowSelected || isProcessing}
                className="share"
                title={translations.IMPORT_EXPORT_generate_sharing_token}
            >
                {translations.IMPORT_EXPORT_generate_sharing_token}
            </ButtonPrimary>
            <ButtonPrimary
                onClick={onExportFlow}
                disabled={!isFlowSelected || isProcessing}
                title={translations.IMPORT_EXPORT_download_package}
            >
                {translations.IMPORT_EXPORT_download_package}
            </ButtonPrimary>
            <div className="margin-top">
                <FormGroup label={translations.IMPORT_EXPORT_sharing_token_label}>
                    <ClipboardInput
                        value={sharingToken || ''}
                        placeholder={translations.IMPORT_EXPORT_sharing_token_placeholder}
                        inputClassName="sharing-token form-control with-extension"
                    />
                </FormGroup>
            </div>
        </div>
    );
};

export default ExportFlow;

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'environmentsLoaded':
            return {
                ...state,
                environments: action.payload,
            };
        case 'environmentSelected':
            return {
                ...state,
                selectedEnvironmentId: action.payload,
                selectedFlowId: null,
                flows: [],
            };
        case 'flowsLoaded':
            return {
                ...state,
                flows: action.payload,
            };
        case 'flowSelected':
            return {
                ...state,
                selectedFlowId: action.payload,
            };
        case 'setIncludePasswords':
            return {
                ...state,
                includePasswords: action.payload,
            };
        case 'setIsProcessing':
            return {
                ...state,
                isProcessing: action.payload,
            };
        case 'sharingTokenLoaded':
            return {
                ...state,
                isProcessing: false,
                sharingToken: action.payload,
            };
        case 'sharingTokenFailed':
            return {
                ...state,
                isProcessing: false,
                sharingToken: null,
            };
    }
}

type State = {
    environments: Environment[];
    selectedEnvironmentId: string | null;
    flows: FlowResponseAPI[];
    selectedFlowId: string | null;
    sharingToken: string | null;
    includePasswords: boolean;
    isProcessing: boolean;
};

type Action =
    | {
          type: 'setIsProcessing';
          payload: boolean;
      }
    | {
          type: 'environmentsLoaded';
          payload: Environment[];
      }
    | {
          type: 'environmentSelected';
          payload: string | null;
      }
    | {
          type: 'flowsLoaded';
          payload: FlowResponseAPI[];
      }
    | {
          type: 'flowSelected';
          payload: string | null;
      }
    | {
          type: 'setIncludePasswords';
          payload: boolean;
      }
    | {
          type: 'sharingTokenLoaded';
          payload: string | null;
      }
    | {
          type: 'sharingTokenFailed';
      };
